import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Table, Button, Typography, Row, Col, Statistic, message, Alert, Tag, Space,
} from 'antd';
import {
  ReloadOutlined, WarningOutlined, CloudOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { ecologyApi, AQIReading, EcologyAlert } from '../../api/ecology';

const { Title, Text } = Typography;

const getAQIColor = (aqi: number): string => {
  if (aqi <= 50) return 'var(--l-ok)';
  if (aqi <= 100) return 'var(--l-warn)';
  if (aqi <= 150) return 'var(--l-warn)';
  return 'var(--l-danger)';
};

const getAQILabel = (aqi: number): string => {
  if (aqi <= 50) return 'Хорошо';
  if (aqi <= 100) return 'Умеренно';
  if (aqi <= 150) return 'Вредно для чувствительных';
  if (aqi <= 200) return 'Вредно';
  if (aqi <= 300) return 'Очень вредно';
  return 'Опасно';
};

const EcologyPage: React.FC = () => {
  const [latest, setLatest] = useState<AQIReading | null>(null);
  const [history, setHistory] = useState<AQIReading[]>([]);
  const [alerts, setAlerts] = useState<EcologyAlert[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [messageApi, contextHolder] = message.useMessage();

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [latestRes, historyRes, alertsRes] = await Promise.allSettled([
        ecologyApi.getLatest(),
        ecologyApi.getHistory({ days: 7 }),
        ecologyApi.getAlerts(),
      ]);
      if (latestRes.status === 'fulfilled') setLatest(latestRes.value.data);
      if (historyRes.status === 'fulfilled') setHistory(historyRes.value.data);
      if (alertsRes.status === 'fulfilled') setAlerts(alertsRes.value.data);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Ошибка загрузки');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const alertColumns: ColumnsType<EcologyAlert> = [
    {
      title: 'Тип',
      dataIndex: 'alert_type',
      key: 'alert_type',
      width: 140,
      render: (type: string) => <Tag icon={<WarningOutlined />}>{type}</Tag>,
    },
    {
      title: 'AQI',
      dataIndex: 'aqi_value',
      key: 'aqi_value',
      width: 80,
      render: (v: number) => v ? <Text strong style={{ color: getAQIColor(v) }}>{v}</Text> : '--',
    },
    {
      title: 'Сообщение',
      dataIndex: 'message_ru',
      key: 'message_ru',
      ellipsis: true,
    },
    {
      title: 'Дата',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 140,
      render: (d: string) => dayjs(d).format('DD.MM.YYYY HH:mm'),
    },
  ];

  const aqiValue = latest?.aqi ?? 0;

  return (
    <div>
      {contextHolder}

      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <Title level={3} style={{ margin: 0, color: 'var(--l-ink)' }}>Экология</Title>
          <Text type="secondary">Качество воздуха и экологические показатели города Аксай</Text>
        </div>
        <Button icon={<ReloadOutlined />} onClick={fetchData}>
          Обновить
        </Button>
      </div>

      {error && <Alert type="error" message={error} closable onClose={() => setError(null)} style={{ marginBottom: 16 }} />}

      {/* AQI Gauge Card */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} md={8}>
          <Card style={{ textAlign: 'center', borderLeft: `4px solid ${getAQIColor(aqiValue)}` }}>
            <div style={{ marginBottom: 8 }}>
              <CloudOutlined style={{ fontSize: 32, color: getAQIColor(aqiValue) }} />
            </div>
            <Statistic
              title="Индекс качества воздуха (AQI)"
              value={latest ? aqiValue : '--'}
              valueStyle={{ fontSize: 48, color: getAQIColor(aqiValue), fontWeight: 700 }}
            />
            <Tag color={getAQIColor(aqiValue)} style={{ marginTop: 8, fontSize: 14, padding: '4px 12px' }}>
              {latest ? getAQILabel(aqiValue) : 'Нет данных'}
            </Tag>
            {latest?.station_name && (
              <div style={{ marginTop: 8 }}>
                <Text type="secondary">Станция: {latest.station_name}</Text>
              </div>
            )}
            {latest?.measured_at && (
              <div style={{ marginTop: 4 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Обновлено: {dayjs(latest.measured_at).format('DD.MM.YYYY HH:mm')}
                </Text>
              </div>
            )}
          </Card>
        </Col>
        <Col xs={24} md={16}>
          <Row gutter={16}>
            <Col xs={12} sm={6}>
              <Card>
                <Statistic title="PM2.5" value={latest?.pm25 ?? '--'} suffix="мкг/м3" valueStyle={{ fontSize: 24 }} />
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card>
                <Statistic title="PM10" value={latest?.pm10 ?? '--'} suffix="мкг/м3" valueStyle={{ fontSize: 24 }} />
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card>
                <Statistic title="Температура" value={latest?.temperature ?? '--'} suffix="°C" valueStyle={{ fontSize: 24 }} />
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card>
                <Statistic title="Влажность" value={latest?.humidity ?? '--'} suffix="%" valueStyle={{ fontSize: 24 }} />
              </Card>
            </Col>
          </Row>
          <Card style={{ marginTop: 16 }}>
            <Title level={5}>Шкала AQI</Title>
            <Space direction="vertical" size={4}>
              <Text><span style={{ display: 'inline-block', width: 16, height: 16, background: 'var(--l-ok)', borderRadius: 4, marginRight: 8, verticalAlign: 'middle' }} />0-50 — Хорошо</Text>
              <Text><span style={{ display: 'inline-block', width: 16, height: 16, background: 'var(--l-warn)', borderRadius: 4, marginRight: 8, verticalAlign: 'middle' }} />51-100 — Умеренно</Text>
              <Text><span style={{ display: 'inline-block', width: 16, height: 16, background: 'var(--l-warn)', borderRadius: 4, marginRight: 8, verticalAlign: 'middle' }} />101-150 — Вредно для чувствительных групп</Text>
              <Text><span style={{ display: 'inline-block', width: 16, height: 16, background: 'var(--l-danger)', borderRadius: 4, marginRight: 8, verticalAlign: 'middle' }} />151+ — Вредно для здоровья</Text>
            </Space>
          </Card>
        </Col>
      </Row>

      {/* Alerts */}
      <Card title="Экологические оповещения" bodyStyle={{ padding: 0 }} style={{ marginBottom: 24 }}>
        <Table<EcologyAlert> rowKey="id" columns={alertColumns} dataSource={alerts} loading={loading} pagination={{ pageSize: 10, showTotal: t => `Всего: ${t}` }} locale={{ emptyText: 'Оповещений нет' }} scroll={{ x: 600 }} />
      </Card>

      {/* Data source info */}
      <Card>
        <Text type="secondary">
          Данные о качестве воздуха обновляются автоматически каждый час. Источник: датчики мониторинга и сервис AirVisual.
        </Text>
      </Card>
    </div>
  );
};

export default EcologyPage;
